// src/features/mensagens/hooks/useClientes.js
// ============================================================================
// 🎣 HOOK — Búsqueda y Creación de Clientes desde Mensagens
// 📦 V2 ENTERPRISE — userId Explícito · Anti-Crash · Zero Ruptura
// ============================================================================
//
// REGLAS DE ORO:
// 1. Reutiliza DataContext + database.js existente
// 2. V2: userId se pasa explícitamente o se resuelve de localStorage
// 3. Operaciones con try/catch en cascada — nunca crashea
// ============================================================================

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { getMensagensService } from '../api/mensagensService';
import { validateTelefone } from '../utils/validators';

// ============================================================================
// 🎯 HOOK PRINCIPAL
// ============================================================================

/**
 * Hook para buscar y crear clientes desde el módulo de mensagens
 * 
 * @param {Object} options
 * @param {string} [options.userId] - ID del usuario (opcional, se resuelve de localStorage)
 * @returns {Object} Estado y acciones de clientes
 */
export function useClientes({ userId: propUserId } = {}) {
  // --------------------------------------------------------------------------
  // 📦 ESTADO
  // --------------------------------------------------------------------------
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --------------------------------------------------------------------------
  // 🔧 REFERENCIAS
  // --------------------------------------------------------------------------
  const serviceRef = useRef(null);
  const mountedRef = useRef(true);

  // --------------------------------------------------------------------------
  // 🔐 RESOLVER userId (V2 — Ultra Robusto)
  // --------------------------------------------------------------------------

  const resolvedUserId = useMemo(() => {
    // 1. Prop explícita (prioridad máxima)
    if (propUserId) return propUserId;

    // 2. localStorage auth_user
    try {
      const stored = localStorage.getItem('auth_user');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.id) return parsed.id;
      }
    } catch {}

    // 3. Firebase Auth global
    try {
      if (window.firebase?.auth?.currentUser?.uid) {
        return window.firebase.auth.currentUser.uid;
      }
    } catch {}

    // 4. localStorage imoveispro_db
    try {
      const db = JSON.parse(localStorage.getItem('imoveispro_db') || '{}');
      const users = db.users || [];
      if (users.length > 0 && users[0]?.id) {
        return users[0].id;
      }
    } catch {}

    return null;
  }, [propUserId]);

  // --------------------------------------------------------------------------
  // 🏗️ INICIALIZACIÓN
  // --------------------------------------------------------------------------

  useEffect(() => {
    serviceRef.current = getMensagensService(resolvedUserId || undefined);

    console.log('🔌 [useClientes] Servicio inicializado', {
      hasUserId: !!resolvedUserId,
      userId: resolvedUserId ? `${resolvedUserId.slice(0, 8)}...` : 'no resuelto',
    });

    return () => {
      mountedRef.current = false;
    };
  }, [resolvedUserId]);

  // --------------------------------------------------------------------------
  // 📥 CARGA DE CLIENTES
  // --------------------------------------------------------------------------

  const loadClients = useCallback(async () => {
    const service = serviceRef.current;
    if (!service) {
      console.warn('⚠️ [useClientes] Servicio no inicializado');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const ctx = await service._getContext();

      let clients = [];
      if (ctx?.collections?.clients?.read) {
        clients = await ctx.collections.clients.read();
      } else {
        // Fallback: database.js
        const database = ctx?._database || ctx?.database;
        if (database?.getClients) {
          clients = await database.getClients();
        }
      }

      const safeClients = Array.isArray(clients) ? clients : [];

      if (mountedRef.current) {
        setAllClients(safeClients);
        console.log(`✅ [useClientes] ${safeClients.length} clientes cargados`);
      }
    } catch (err) {
      console.error('❌ [useClientes] Error cargando clientes:', err);
      if (mountedRef.current) {
        setError(err.message || 'Error al cargar clientes');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Carga inicial
  useEffect(() => {
    if (resolvedUserId !== undefined) {
      loadClients();
    }
  }, [resolvedUserId, loadClients]);

  // --------------------------------------------------------------------------
  // 🔍 BÚSQUEDA
  // --------------------------------------------------------------------------

  const searchClients = useCallback((query) => {
    if (!query?.trim()) return allClients;

    const term = query.trim().toLowerCase();
    const searchDigits = term.replace(/\D/g, '');

    return allClients.filter(c => {
      const name = (c.name || '').toLowerCase();
      const phone = (c.phone || '').replace(/\D/g, '');
      const email = (c.email || '').toLowerCase();

      return (
        name.includes(term) ||
        phone.includes(searchDigits) ||
        email.includes(term)
      );
    });
  }, [allClients]);

  // --------------------------------------------------------------------------
  // ➕ CREAR CLIENTE
  // --------------------------------------------------------------------------

  const createClient = useCallback(async ({ name, phone, email = '' }) => {
    if (!name || !phone) {
      throw new Error('Nome e telefone são obrigatórios');
    }

    // Validar teléfono
    const phoneValidation = validateTelefone(phone);
    if (!phoneValidation.valid) {
      throw new Error(phoneValidation.error);
    }

    const service = serviceRef.current;
    if (!service) {
      throw new Error('Servicio no inicializado');
    }

    const ctx = await service._getContext();

    let newClient = null;

    // Estrategia 1: DataContext
    try {
      if (ctx?.collections?.clients?.create) {
        newClient = await ctx.collections.clients.create({
          name: name.trim(),
          phone: phoneValidation.normalized,
          email: (email || '').trim(),
          createdAt: Date.now(),
        });
      }
    } catch (err) {
      console.warn('⚠️ [useClientes] Error creando via DataContext:', err.message);
    }

    // Estrategia 2: database.js directo
    if (!newClient) {
      try {
        const database = ctx?._database || ctx?.database;
        if (database?.createClient) {
          newClient = await database.createClient({
            name: name.trim(),
            phone: phoneValidation.normalized,
            email: (email || '').trim(),
          });
        }
      } catch (err) {
        console.warn('⚠️ [useClientes] Error en fallback database.js:', err.message);
      }
    }

    // Estrategia 3: Cliente local (último recurso)
    if (!newClient) {
      console.warn('⚠️ [useClientes] Creando cliente solo en memoria');
      newClient = {
        id: `local_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        name: name.trim(),
        phone: phoneValidation.normalized,
        email: (email || '').trim(),
        createdAt: Date.now(),
        _local: true,
      };
    }

    // Actualizar lista local
    if (mountedRef.current) {
      setAllClients(prev => {
        // Evitar duplicados
        const exists = prev.some(c => c.id === newClient.id);
        if (exists) return prev;
        return [newClient, ...prev];
      });
    }

    console.log('✅ [useClientes] Cliente creado:', newClient.name);
    return newClient;
  }, []);

  // --------------------------------------------------------------------------
  // 📱 CLIENTES CON TELÉFONO
  // --------------------------------------------------------------------------

  const getClientsWithPhone = useCallback(() => {
    return allClients.filter(c => {
      const digits = (c.phone || '').replace(/\D/g, '');
      return digits.length >= 10;
    });
  }, [allClients]);

  // --------------------------------------------------------------------------
  // 📤 RETORNO
  // --------------------------------------------------------------------------

  return {
    // Datos
    allClients,
    loading,
    error,

    // Acciones
    searchClients,
    createClient,
    getClientsWithPhone,
    reload: loadClients,
    refresh: loadClients,
  };
}

// ============================================================================
// 📤 EXPORTACIÓN DEFAULT
// ============================================================================

export default useClientes;